import React from 'react';

/**
 * FeeAssumptionsCard — "We found additional charges on your document."
 *
 * Sibling of VendorCaptureCard, rendered directly beneath it in the lower
 * portion of the add-items review modal when the AI invoice reader itemized
 * non-product charges (sales tax, delivery, fuel surcharge, minimum-order
 * fee, deposits, credits) on an analyzed upload. Lists what was detected and,
 * when the reader also produced a vendor fee-settings fill plan, offers a
 * per-fee "save to {vendor}" checkbox (default checked). Only BLANK vendor
 * settings are ever filled server-side — user edits always win.
 *
 * Purely presentational + controlled, exactly like VendorCaptureCard: the
 * mount wrapper owns the choice state and forwards the final choices to the
 * approve handler.
 *
 * captures: [{
 *   key,          // stable id for this capture (per source document)
 *   supplierName, // vendor name as read off the document
 *   sourceFile,   // file the charges were read from (shown when > 1 file)
 *   charges,      // [{ type, label, amount }] — the itemized charges
 *   plan,         // [{ column, value, chargeType, label }] — vendor fee fill plan (may be empty)
 *   choices,      // { [column]: boolean } — per-fee accept state (controlled)
 * }]
 * onToggleFee(key, column, checked)
 */

const C = {
  text: '#5A4A42',
  muted: '#8B7B73',
  border: '#E8DBCE',
  bg: '#FFFDF9',
  innerBg: '#FCFAF8',
  innerBorder: '#EFE7E0',
  green: '#2F5D3A',
  greenBg: '#E3F0E4',
  greenBorder: '#B9DBBE',
  amber: '#7A5A0A',
  amberBg: '#FFF0BD',
  amberBorder: '#E6CD83',
};

const CHARGE_TYPE_LABELS = {
  sales_tax: 'Sales tax',
  delivery: 'Delivery',
  fuel_surcharge: 'Fuel surcharge',
  minimum_order_fee: 'Minimum-order fee',
  deposit: 'Deposit',
  credit: 'Credit',
  other: 'Other charge',
};

const FEE_COLUMN_LABELS = {
  delivery_fee: 'Delivery fee',
  fuel_surcharge_amount: 'Fuel surcharge',
  below_minimum_fee: 'Small-order fee',
  sales_tax_rate: 'Sales tax rate',
};

function chargeTypeLabel(type) {
  return CHARGE_TYPE_LABELS[type] || CHARGE_TYPE_LABELS.other;
}

function fmtMoney(value) {
  // NULL means unknown, never $0.00 — Number(null) is a finite 0, so guard
  // nullish first; a label-only charge renders its label with no amount.
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : null;
}

/** sales_tax_rate is STORED as a fraction (0.0825) — displayed as a percent. */
function fmtFeeValue(column, value) {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (column === 'sales_tax_rate') {
    const pct = Math.round(n * 10000) / 100;
    return `${pct}%`;
  }
  return fmtMoney(n);
}

function ChargeRow({ charge }) {
  const label = String(charge?.label || '').trim();
  const typeLabel = chargeTypeLabel(charge?.type);
  const isCredit = charge?.type === 'credit';
  const amount = fmtMoney(charge?.amount);
  return (
    <React.Fragment>
      <dt style={{ color: C.muted, fontSize: '0.74rem', fontWeight: 700 }}>{typeLabel}</dt>
      <dd style={{
        color: C.text, fontSize: '0.78rem', margin: 0, minWidth: 0, wordBreak: 'break-word',
        display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <span style={{ minWidth: 0 }}>{label || typeLabel}</span>
        {amount != null && (
          <span style={{ fontWeight: 700, whiteSpace: 'nowrap', color: isCredit ? C.green : C.text }}>
            {isCredit ? `−${amount}` : amount}
          </span>
        )}
      </dd>
    </React.Fragment>
  );
}

function SingleFeeCapture({ capture, onToggleFee, showSource }) {
  const { key, supplierName, sourceFile } = capture;
  const charges = Array.isArray(capture.charges) ? capture.charges.filter(Boolean) : [];
  const plan = Array.isArray(capture.plan) ? capture.plan.filter((p) => p && p.column) : [];
  const choices = capture.choices || {};
  if (charges.length === 0) return null; // nothing detected on this file — skip entirely
  const vendorLabel = supplierName || 'this vendor';

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>
          {supplierName || 'Unnamed supplier'}
        </span>
        {showSource && sourceFile && (
          <span style={{ color: C.muted, fontSize: '0.74rem' }}>from {sourceFile}</span>
        )}
      </div>

      <div style={{
        marginTop: '0.55rem', background: C.innerBg,
        border: `1px solid ${C.innerBorder}`, borderRadius: 8, padding: '0.6rem 0.75rem',
      }}>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 0.75rem', margin: 0 }}>
          {charges.map((charge, idx) => (
            <ChargeRow key={idx} charge={charge} />
          ))}
        </dl>
      </div>

      {plan.length > 0 && (
        <div style={{ marginTop: '0.55rem', display: 'grid', gap: '0.25rem' }}>
          <div style={{ color: C.muted, fontSize: '0.74rem', fontWeight: 700 }}>
            Save to {vendorLabel}?
          </div>
          {plan.map((entry) => {
            const checked = choices[entry.column] !== false; // default checked
            const feeLabel = FEE_COLUMN_LABELS[entry.column] || entry.column;
            const feeValue = fmtFeeValue(entry.column, entry.value);
            return (
              <label
                key={entry.column}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.5rem 0.65rem', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${checked ? '#C8B7A6' : 'transparent'}`,
                  background: checked ? '#FFF' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggleFee && onToggleFee(key, entry.column, e.target.checked)}
                  style={{ marginTop: 2, accentColor: '#5A4A42', cursor: 'pointer' }}
                />
                <span style={{ flex: 1, minWidth: 0, fontSize: '0.84rem', color: C.text }}>
                  <strong>{feeLabel}{feeValue != null ? ` ${feeValue}` : ''}</strong>
                  {' — fills only blank settings; edit anytime in Vendors'}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FeeAssumptionsCard({ captures = [], onToggleFee }) {
  const list = captures.filter((c) => c && Array.isArray(c.charges) && c.charges.length > 0);
  if (list.length === 0) return null;
  const showSource = list.length > 1;
  return (
    <div
      role="group"
      aria-label="Additional charges found on your document"
      style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: '0.8rem 1rem 0.9rem', margin: '0.9rem 0 0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span aria-hidden="true">🧾</span>
        <span style={{ fontWeight: 800, color: C.text, fontSize: '0.92rem' }}>
          {showSource
            ? 'We found additional charges on your documents'
            : `We found additional charges on ${list[0].sourceFile || 'your document'}`}
        </span>
      </div>
      <p style={{ color: C.muted, fontSize: '0.8rem', margin: '0.3rem 0 0', lineHeight: 1.45 }}>
        Delivery fees, surcharges, tax, and deposits are recorded with the purchase — they never
        change your item prices. Checked fees below fill the vendor's blank settings so future
        orders can anticipate them.
      </p>
      {list.map((capture) => (
        <SingleFeeCapture
          key={capture.key}
          capture={capture}
          onToggleFee={onToggleFee}
          showSource={showSource}
        />
      ))}
    </div>
  );
}
